import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  X,
  Pencil,
  MapPin,
  Clock,
  User,
  Users,
  FileText,
  Link,
  ExternalLink,
  Check,
  CheckSquare,
  ChevronDown,
  ChevronUp,
  Repeat,
} from 'lucide-react';
import { Modal, Badge, ProfileBadge, TaskEventDateIcon } from '@/components/ui';
import { TaskCard } from '@/components/domain/shared/TaskCard';
import {
  getChildrenFromTask,
  getChildrenFromEvent,
  getMembersFromTask,
  getMembersFromEvent,
  type TaskRow,
  type EventRow,
  type ChildRef,
  type MemberRef,
} from '@/lib/schedule';
import {
  formatLongDateRange,
  spansMultipleDays,
  formatTimeRange,
  isAllDaySpan,
  isDateOnlyIso,
} from '@/lib/date';
import { extractUrls, type LinkEntry } from '@/lib/links';
import { seriesLabel } from '@/lib/recurrence';

export type DetailItem =
  | { kind: 'task'; data: TaskRow }
  | { kind: 'event'; data: EventRow };

export interface TaskEventDetailModalProps {
  /** The tapped task/event with its joined children/members. null = closed. */
  item: DetailItem | null;
  /** Primary users get the edit pencil and an interactive completion checkbox. */
  canEdit: boolean;
  /** Resolved title of a task's related event; renders the category chip. */
  relatedEventTitle?: string;
  timezone?: string | null;
  onClose: () => void;
  /** Parent opens the existing ItemFormModal. */
  onEdit: () => void;
  /** Primary-only: persists the completion toggle. */
  onToggleComplete?: (taskId: string, currentStatus: string) => void;
  /** Related sub-tasks to show under an event detail (History View Event). */
  subtasks?: TaskRow[];
  /**
   * De-emphasize the completion indicator (gray, not green) and the event
   * title (muted) for past/History items — YAR-257. Active views (Today/
   * Schedule) leave this false to keep the vivid "done" state.
   */
  muted?: boolean;
}

interface ChipData {
  name: string;
  emoji?: string;
  color?: string;
  imagePath?: string | null;
}

function childToChip(c: ChildRef): ChipData {
  return {
    name: c.name,
    emoji: c.emoji_avatar ?? undefined,
    color: c.emoji_color ?? undefined,
    imagePath: c.avatar_image_path ?? undefined,
  };
}

function memberToChip(m: MemberRef): ChipData {
  return {
    name: m.display_name ?? m.email ?? 'Member',
    emoji: m.avatar_emoji ?? undefined,
    color: m.avatar_color ?? undefined,
    imagePath: m.avatar_image_path ?? undefined,
  };
}

export function TaskEventDetailModal({
  item,
  canEdit,
  relatedEventTitle,
  timezone,
  onClose,
  onEdit,
  onToggleComplete,
  subtasks,
  muted = false,
}: TaskEventDetailModalProps) {
  if (!item) return null;

  const isTask = item.kind === 'task';
  const headerTitle = isTask ? 'Task Details' : 'Event Details';
  const data = item.data;

  const dateSource = isTask
    ? (data as TaskRow).due_date
    : (data as EventRow).start_date;
  // A multi-day event carries no time, anywhere (YAR-420). Without this gate
  // the pill printed the stored zero-duration guard as "12:00 PM–12:00 PM",
  // or relabelled a widened span "All day" — both assert a duration nobody
  // wrote. The date range already says everything the parent needs.
  const spansDays = spansMultipleDays(dateSource, data.end_date, timezone);
  const showTime = !!dateSource && !isDateOnlyIso(dateSource) && !spansDays;
  // YAR-391: the pill printed only the start, so confirming the Loyola
  // tuition period hid its Dec 28 end again right where the user went to
  // check it. `formatLongDateRange` also carries the zone the old
  // `formatDetailDate` silently dropped.
  const allDay =
    !!dateSource && isAllDaySpan(dateSource, data.end_date, timezone);

  // One occurrence of a recurring series (YAR-443).
  const repeatLabel = isTask ? null : seriesLabel(data as EventRow);

  const childChips = (
    isTask
      ? getChildrenFromTask(data as TaskRow)
      : getChildrenFromEvent(data as EventRow)
  ).map(childToChip);
  const memberChips = (
    isTask
      ? getMembersFromTask(data as TaskRow)
      : getMembersFromEvent(data as EventRow)
  ).map(memberToChip);

  // Prefer the stored links array (with titles); fall back to URLs derived
  // from the scalar link + notes for rows not yet on the array shape.
  const links: LinkEntry[] =
    data.links && data.links.length > 0
      ? data.links
      : extractUrls(data.link, data.notes).map((url) => ({ title: null, url }));

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      bare
      ariaLabel={headerTitle}
    >
      {/* Header: X (left) · title (center) · pencil (right, primary only) */}
      <div className="flex items-center justify-between px-5 py-4 shrink-0">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="inline-flex items-center justify-center rounded-md p-1 text-foreground hover:bg-muted transition-colors duration-150 cursor-pointer"
        >
          <X size={22} />
        </button>
        <h2 className="text-base font-semibold text-foreground">
          {headerTitle}
        </h2>
        {canEdit ? (
          <button
            type="button"
            onClick={onEdit}
            aria-label="Edit"
            className="inline-flex items-center justify-center rounded-md p-1 text-foreground hover:bg-muted transition-colors duration-150 cursor-pointer"
          >
            <Pencil size={20} />
          </button>
        ) : (
          <span
            className="w-8"
            aria-hidden="true"
          />
        )}
      </div>

      {/* Body */}
      <div className="flex flex-col gap-5 px-5 pt-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] overflow-y-auto flex-1">
        {isTask && relatedEventTitle && (
          <div>
            <Badge
              variant="info"
              size="sm"
            >
              {relatedEventTitle}
            </Badge>
          </div>
        )}

        <TitleRow
          isTask={isTask}
          title={data.title}
          status={isTask ? (data as TaskRow).status : undefined}
          taskId={isTask ? data.id : undefined}
          onToggleComplete={onToggleComplete}
          muted={muted}
        />

        {dateSource && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1 text-sm text-foreground">
              <TaskEventDateIcon
                size={14}
                className="text-muted-foreground"
              />
              {formatLongDateRange(dateSource, data.end_date, timezone)}
            </span>
            {showTime && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1 text-sm text-foreground">
                <Clock
                  size={14}
                  className="text-muted-foreground"
                />
                {allDay
                  ? 'All day'
                  : formatTimeRange(dateSource, data.end_date, timezone)}
              </span>
            )}
            {repeatLabel && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1 text-sm text-foreground">
                <Repeat
                  size={14}
                  className="text-muted-foreground"
                  aria-hidden="true"
                />
                {repeatLabel}
              </span>
            )}
          </div>
        )}

        {data.location && (
          <DetailRow
            icon={<MapPin size={18} />}
            label="Location"
          >
            {/^https?:\/\//.test(data.location) ? (
              <a
                href={data.location}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary underline underline-offset-2 break-words"
              >
                {data.location}
              </a>
            ) : (
              <span className="text-sm text-foreground underline underline-offset-2 break-words">
                {data.location}
              </span>
            )}
          </DetailRow>
        )}

        {childChips.length > 0 && (
          <DetailRow
            icon={<User size={18} />}
            label="Children"
          >
            <ChipOverflow chips={childChips} />
          </DetailRow>
        )}

        {memberChips.length > 0 && (
          <DetailRow
            icon={<Users size={18} />}
            label="Members"
          >
            <ChipOverflow chips={memberChips} />
          </DetailRow>
        )}

        {data.notes && (
          <DetailRow
            icon={<FileText size={18} />}
            label="Notes"
          >
            <ExpandableText text={data.notes} />
          </DetailRow>
        )}

        {links.length > 0 && (
          <DetailRow
            icon={<Link size={18} />}
            label="Links"
          >
            <LinkList links={links} />
          </DetailRow>
        )}

        {!isTask && subtasks && subtasks.length > 0 && (
          <SubtasksSection
            subtasks={subtasks}
            timezone={timezone}
          />
        )}
      </div>
    </Modal>
  );
}

// ── Title + completion checkbox ─────────────────────────────────────

function TitleRow({
  isTask,
  title,
  status,
  taskId,
  onToggleComplete,
  muted,
}: {
  isTask: boolean;
  title: string;
  status?: string;
  taskId?: string;
  onToggleComplete?: (taskId: string, currentStatus: string) => void;
  muted?: boolean;
}) {
  const initialDone = status === 'DONE' || status === 'CANCELLED';
  const [completed, setCompleted] = useState(initialDone);
  const canToggle = isTask && !!onToggleComplete && !!taskId && !!status;

  // History/past de-emphasizes the completed ring to gray (#f5f5f5) with a
  // dark check; active views keep the vivid green "done" fill (YAR-257).
  const completedRing = muted
    ? 'bg-surface-muted border-border-strong'
    : 'bg-primary border-primary';
  // The muted check is #7a7a7a (text/placeholder), not #5e5e5e
  // (muted-foreground) — QA measured it off Figma on Jul 21 (YAR-257).
  const checkColor = muted
    ? 'text-text-placeholder'
    : 'text-primary-foreground';

  const circle = (
    <span
      className={[
        'mt-1 w-6 h-6 rounded-full border inline-flex items-center justify-center shrink-0',
        completed ? completedRing : 'bg-background border-border',
      ].join(' ')}
    >
      {completed && (
        <Check
          size={14}
          className={checkColor}
        />
      )}
    </span>
  );

  return (
    <div className="flex items-start gap-3">
      {isTask &&
        (canToggle ? (
          <button
            type="button"
            aria-label={completed ? 'Mark incomplete' : 'Mark complete'}
            onClick={() => {
              // Report the status the task is in NOW. Completing used to
              // report a hardcoded 'TODO', so an IN_PROGRESS task lost its
              // status: the page could not know what Undo should restore
              // (YAR-476). Unchecking reports DONE and the page restores the
              // status it remembered (TODO when it has none — History).
              onToggleComplete!(
                taskId!,
                completed ? 'DONE' : initialDone ? 'TODO' : status!,
              );
              setCompleted((c) => !c);
            }}
            className={[
              'mt-1 w-6 h-6 rounded-full border inline-flex items-center justify-center shrink-0 cursor-pointer',
              completed ? completedRing : 'bg-background border-border',
            ].join(' ')}
          >
            {completed && (
              <Check
                size={14}
                className={checkColor}
              />
            )}
          </button>
        ) : (
          circle
        ))}
      <h1
        className={[
          'text-2xl font-bold break-words',
          // Event titles read as muted/past in History (YAR-257); task titles
          // stay full strength and gain line-through when completed.
          !isTask && muted ? 'text-muted-foreground' : 'text-foreground',
          completed && isTask ? 'line-through opacity-60' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {title}
      </h1>
    </div>
  );
}

// ── Label + value row ───────────────────────────────────────────────

function DetailRow({
  icon,
  label,
  children,
}: {
  icon: ReactNode;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex items-center gap-2 w-28 shrink-0">
        <span className="text-muted-foreground">{icon}</span>
        <span className="text-sm font-medium text-foreground">{label}</span>
      </div>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

// ── Chip list with +N overflow popover ──────────────────────────────

function ChipOverflow({
  chips,
  maxVisible = 2,
}: {
  chips: ChipData[];
  maxVisible?: number;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const visible = chips.slice(0, maxVisible);
  const overflow = chips.slice(maxVisible);

  return (
    <div
      ref={ref}
      className="relative flex flex-wrap items-center gap-1.5"
    >
      {visible.map((chip, i) => (
        <ProfileBadge
          key={`${chip.name}-${i}`}
          name={chip.name}
          emoji={chip.emoji}
          color={chip.color}
          imagePath={chip.imagePath}
        />
      ))}
      {overflow.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-label={`Show ${overflow.length} more`}
            aria-expanded={open}
            className="inline-flex items-center rounded-full border border-border bg-background px-2.5 py-0.5 text-xs font-medium text-foreground cursor-pointer hover:bg-muted"
          >
            +{overflow.length}
          </button>
          {open && (
            <div
              role="list"
              className="absolute top-full left-0 mt-1 z-[200] flex flex-col gap-1.5 rounded-lg border border-border bg-card p-2 shadow-[0px_0px_16px_-4px_rgba(56,64,74,0.1)]"
            >
              {overflow.map((chip, i) => (
                <span
                  role="listitem"
                  key={`${chip.name}-of-${i}`}
                >
                  <ProfileBadge
                    name={chip.name}
                    emoji={chip.emoji}
                    color={chip.color}
                    imagePath={chip.imagePath}
                  />
                </span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Notes with See more / See less ──────────────────────────────────

function ExpandableText({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = text.length > 140 || text.includes('\n');

  return (
    <div className="flex flex-col gap-1">
      <p
        className={[
          'text-sm text-foreground whitespace-pre-wrap break-words',
          !expanded && isLong ? 'line-clamp-3' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {text}
      </p>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="inline-flex items-center gap-1 self-start text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground"
        >
          {expanded ? 'See less' : 'See more'}
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      )}
    </div>
  );
}

// ── Sub-tasks (event detail) ────────────────────────────────────────

function SubtasksSection({
  subtasks,
  timezone,
}: {
  subtasks: TaskRow[];
  timezone?: string | null;
}) {
  const [expanded, setExpanded] = useState(true);
  return (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        className="inline-flex items-center gap-2 self-start text-sm font-semibold text-foreground cursor-pointer"
      >
        <CheckSquare
          size={18}
          className="text-muted-foreground"
        />
        Sub-tasks
        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {expanded && (
        <div className="flex flex-col gap-2">
          {subtasks.map((t) => (
            <TaskCard
              key={t.id}
              id={t.id}
              title={t.title}
              description={t.notes ?? undefined}
              dueDate={t.due_date ?? undefined}
              status={t.status as 'TODO' | 'IN_PROGRESS' | 'DONE' | 'CANCELLED'}
              children={getChildrenFromTask(t).map((c) => ({
                name: c.name,
                emoji: c.emoji_avatar ?? undefined,
                color: c.emoji_color ?? undefined,
                imagePath: c.avatar_image_path ?? undefined,
              }))}
              timezone={timezone}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Links with See more / See less ──────────────────────────────────

function LinkList({
  links,
  maxVisible = 3,
}: {
  links: LinkEntry[];
  maxVisible?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const hasOverflow = links.length > maxVisible;
  const visible = expanded ? links : links.slice(0, maxVisible);

  return (
    <div className="flex flex-col gap-2">
      {visible.map((link, i) => (
        <a
          key={`${link.url}-${i}`}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm text-primary underline underline-offset-2 break-all"
        >
          <span className="break-all">{link.title || link.url}</span>
          <ExternalLink
            size={12}
            className="shrink-0"
          />
        </a>
      ))}
      {hasOverflow && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="inline-flex items-center gap-1 self-start text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground"
        >
          {expanded ? 'See less' : 'See more'}
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      )}
    </div>
  );
}
